import { useState, useEffect } from "react";
import { DateInput } from "@/components/DateInput";
import { Link } from "react-router-dom";
import {
  ChevronDown, User2, Star, ThumbsUp, Gift, Lock, FileText, Sparkles,
  Save, Cake, MapPin, ArrowRight, Phone,
} from "lucide-react";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader } from "@/components/common";
import { Button } from "@/components/Button";
import { Field, TextInput, TextArea } from "@/components/Modal";
import { ImageUpload } from "@/components/ImageUpload";
import { ProfileCategory } from "@/components/ProfileCategory";

const FAV_SUBS = [
  ["places", "Favorite Places"], ["stores", "Stores"], ["restaurants", "Restaurants"], ["custom", "Custom"],
].map(([value, label]) => ({ value, label }));

const LD_SUBS = [
  ["like", "Things I like"], ["dislike", "Things I dislike"], ["wont_eat", "Foods I won't eat"],
  ["annoys", "Things that annoy me"], ["makes_happy", "Things that make me happy"], ["custom", "Custom"],
].map(([value, label]) => ({ value, label }));

const LD_TOPICS = [
  ["foods", "Foods"], ["snacks", "Snacks"], ["drinks", "Drinks"], ["colors", "Colors"], ["movies", "Movies"],
  ["tv", "TV Shows"], ["music", "Music / Artists"], ["books", "Books"], ["games", "Games"],
  ["hobbies", "Hobbies"], ["custom", "Custom"],
].map(([value, label]) => ({ value, label }));

const GIFT_SUBS = [
  ["clothing", "Clothing size"], ["shoe", "Shoe size"], ["ring", "Ring size"], ["brands", "Favorite brands"],
  ["love", "Things I'd love to receive"], ["avoid", "Things I don't want"], ["other", "Custom"],
].map(([value, label]) => ({ value, label }));

const IMP_SUBS = [
  ["allergies", "Allergies"], ["blood", "Blood type"], ["dietary", "Dietary restrictions"],
  ["medical", "Medical info"], ["religion", "Religion / faith"], ["emergency", "Emergency info"], ["other", "Custom"],
].map(([value, label]) => ({ value, label }));

const FAV_EX = {
  places: { name: "e.g. Subway", detail: "Italian BMT on wheat\nFootlong, extra olives\nHot chocolate, no whip" },
  stores: { name: "e.g. Target", detail: "Home decor aisle\nStarbucks inside" },
  restaurants: { name: "e.g. Olive Garden", detail: "Chicken alfredo\nExtra breadsticks" },
  custom: { name: "Add a favorite" },
};
const LD_EX = {
  like: { name: "e.g. Long walks" }, dislike: { name: "e.g. Loud chewing" }, wont_eat: { name: "e.g. Mushrooms" },
  annoys: { name: "e.g. Being interrupted" }, makes_happy: { name: "e.g. Fresh flowers" }, custom: { name: "Add an entry" },
};
const GIFT_EX = {
  clothing: { name: "e.g. Shirt size", detail: "Medium" }, shoe: { name: "e.g. Shoe size", detail: "US 9" },
  ring: { name: "e.g. Ring size", detail: "Size 7" }, brands: { name: "e.g. Nike", detail: "Loves their hoodies" },
  love: { name: "e.g. Cozy blanket" }, avoid: { name: "e.g. Perfume", detail: "Sensitive to scents" }, other: { name: "Gift note" },
};
const IMP_EX = {
  allergies: { name: "e.g. Peanuts", detail: "Severe" }, blood: { name: "Blood type", detail: "O+" },
  dietary: { name: "e.g. Vegetarian" }, medical: { name: "e.g. Asthma", detail: "Carries an inhaler" },
  religion: { name: "e.g. Catholic" }, emergency: { name: "e.g. Mom — Jane", detail: "+1 555 123 4567" }, other: { name: "Important note" },
};

function Accordion({ id, icon: Icon, title, badge, openId, setOpenId, children }) {
  const open = openId === id;
  return (
    <div className="iks-card mb-3 overflow-hidden transition-all hover:border-sky-500/30">
      <button
        onClick={() => setOpenId(open ? null : id)}
        data-testid={`accordion-${id}`}
        className="w-full flex items-center gap-3 px-5 py-4 text-left"
      >
        <div className="w-9 h-9 rounded-xl bg-sky-500/10 flex items-center justify-center shrink-0">
          <Icon size={18} className="text-sky-400" />
        </div>
        <span className="font-display font-semibold text-slate-100 flex-1">{title}</span>
        {badge}
        <ChevronDown size={18} className={`text-slate-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="px-5 pb-5 pt-1 iks-fade-up">{children}</div>}
    </div>
  );
}

export default function ProfilePage() {
  const { user, refresh } = useAuth();
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState("basic");
  const [profile, setProfile] = useState(null);
  const [items, setItems] = useState([]);
  const [basic, setBasic] = useState({});
  const [about, setAbout] = useState("");
  const [savingBasic, setSavingBasic] = useState(false);
  const [savingAbout, setSavingAbout] = useState(false);

  const load = async () => {
    const [p, it] = await Promise.all([api.get("/profile/me"), api.get("/profile/items")]);
    setProfile(p.data);
    setBasic({ ...p.data.basic, name: p.data.name, profile_photo: user?.profile_photo });
    setAbout(p.data.about_me || "");
    setItems(it.data);
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);

  const reloadItems = async () => {
    const it = await api.get("/profile/items");
    setItems(it.data);
  };

  const catItems = (c) => items.filter((i) => i.category === c);

  const saveBasic = async () => {
    setSavingBasic(true);
    try {
      const { data } = await api.put("/profile/basic", basic);
      setProfile((p) => ({ ...p, basic: data.basic, zodiac: data.zodiac }));
      await refresh();
      toast.success("Basic info saved");
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setSavingBasic(false);
    }
  };

  const saveAbout = async () => {
    setSavingAbout(true);
    try {
      await api.put("/profile/about", { about_me: about });
      toast.success("Saved");
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setSavingAbout(false);
    }
  };

  if (loading) return <Loader />;

  return (
    <div className="iks-fade-up">
      <div className="flex items-center gap-4 mb-6">
        <ImageUpload
          value={basic.profile_photo}
          onChange={async (p) => {
            setBasic((b) => ({ ...b, profile_photo: p }));
            await api.put("/profile/basic", { profile_photo: p });
            await refresh();
          }}
        />
        <div>
          <h1 className="font-display font-bold text-2xl text-slate-50">{user?.name}</h1>
          <p className="text-slate-400 text-sm">@{user?.username}</p>
          {profile?.zodiac && <p className="text-sky-400 text-xs mt-1">{profile.zodiac}</p>}
        </div>
      </div>

      {/* Basic */}
      <Accordion id="basic" icon={User2} title="Basic Information" openId={openId} setOpenId={setOpenId}>
        <Field label="Name" hint="This is what your connections see and call you.">
          <TextInput value={basic.name || ""} onChange={(e) => setBasic((b) => ({ ...b, name: e.target.value }))} data-testid="basic-name" />
        </Field>
        <Field label="Phone" hint="Optional. Lets people find and connect with you by phone.">
          <div className="relative">
            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
            <TextInput type="tel" value={basic.phone || ""} onChange={(e) => setBasic((b) => ({ ...b, phone: e.target.value }))} placeholder="+1 555 123 4567" className="pl-9" data-testid="basic-phone" />
          </div>
        </Field>
        <Field label="Birthday">
          <DateInput value={basic.birthday || ""} onChange={(e) => setBasic((b) => ({ ...b, birthday: e.target.value }))} data-testid="basic-birthday" />
        </Field>
        <Field label="Zodiac (auto)">
          <div className="iks-input w-full px-3.5 py-2.5 text-sm text-slate-400 flex items-center gap-2">
            <Cake size={14} className="text-sky-400 shrink-0" /> <span className="truncate">{profile?.zodiac || "Set your birthday to see this"}</span>
          </div>
        </Field>
        <Field label="Pronouns (optional)">
          <TextInput value={basic.pronouns || ""} onChange={(e) => setBasic((b) => ({ ...b, pronouns: e.target.value }))} placeholder="she/her, he/him, they/them" />
        </Field>
        <Field label="Location (optional)">
          <div className="relative">
            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
            <TextInput value={basic.location || ""} onChange={(e) => setBasic((b) => ({ ...b, location: e.target.value }))} placeholder="City, Country" className="pl-9" />
          </div>
        </Field>
        <Button onClick={saveBasic} loading={savingBasic} data-testid="save-basic-btn"><Save size={16} /> Save</Button>
      </Accordion>

      {/* Favorites */}
      <Accordion id="favorites" icon={Star} title="Favorite Places" openId={openId} setOpenId={setOpenId}>
        <ProfileCategory category="favorites" subOptions={FAV_SUBS} examples={FAV_EX} items={catItems("favorites")} reload={reloadItems} rich emptyIcon={Star} emptyText="No favorite places yet" />
      </Accordion>

      {/* Likes & Dislikes */}
      <Accordion id="likes_dislikes" icon={ThumbsUp} title="Likes & Dislikes" openId={openId} setOpenId={setOpenId}>
        <ProfileCategory category="likes_dislikes" subOptions={LD_SUBS} topics={LD_TOPICS} examples={LD_EX} items={catItems("likes_dislikes")} reload={reloadItems} emptyIcon={ThumbsUp} emptyText="Nothing here yet" />
      </Accordion>

      {/* Gift */}
      <Accordion id="gift" icon={Gift} title="Gift Information" openId={openId} setOpenId={setOpenId}>
        <ProfileCategory category="gift" subOptions={GIFT_SUBS} examples={GIFT_EX} items={catItems("gift")} reload={reloadItems} emptyIcon={Gift} emptyText="No gift info yet" />
      </Accordion>

      {/* Important */}
      <Accordion
        id="important" icon={Lock} title="Important Information" openId={openId} setOpenId={setOpenId}
        badge={<span className="text-[10px] font-semibold uppercase tracking-wide text-amber-300/90 bg-amber-500/10 px-2 py-0.5 rounded-full mr-1">Private</span>}
      >
        <p className="text-xs text-slate-500 mb-3 bg-white/5 rounded-lg px-3 py-2">
          <Lock size={12} className="inline mr-1 text-amber-300" />
          Sensitive by default. This is never shared with a connection unless you explicitly turn it on.
        </p>
        <ProfileCategory category="important" subOptions={IMP_SUBS} examples={IMP_EX} items={catItems("important")} reload={reloadItems} emptyIcon={Lock} emptyText="No sensitive info stored" />
      </Accordion>

      {/* About Me */}
      <Accordion id="about" icon={FileText} title="About Me" openId={openId} setOpenId={setOpenId}>
        <Field label="A few things about me…">
          <TextArea value={about} onChange={(e) => setAbout(e.target.value)} placeholder="Anything you'd like the people you connect with to know." className="min-h-[140px]" data-testid="about-textarea" />
        </Field>
        <Button onClick={saveAbout} loading={savingAbout} data-testid="save-about-btn"><Save size={16} /> Save</Button>
      </Accordion>

      {/* Wishlist link */}
      <Accordion id="wishlist" icon={Sparkles} title="WishList" openId={openId} setOpenId={setOpenId}>
        <p className="text-slate-400 text-sm mb-4">Your personal wishlist lives on its own screen so it's easy to manage and share.</p>
        <Link to="/wishlist">
          <Button variant="accent" data-testid="go-wishlist-btn">Open my WishList <ArrowRight size={16} /></Button>
        </Link>
      </Accordion>
    </div>
  );
}
